import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../../components/common/BackButton';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { getJobs } from '../../../services/jobsService';
import {
  getDocumentTypes,
  generateDocumentBlob,
} from '../../../services/documentsService';
import { getApiErrorMessage } from '../../../services/http';
import {
  DocumentTextIcon,
  ReceiptRefundIcon,
  ShoppingBagIcon,
  TruckIcon,
  CurrencyDollarIcon,
  PlusIcon,
  TrashIcon,
  CalendarIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';

const Documents = () => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState('');
  const [jobs, setJobs] = useState([]);
  const [showQuotationForm, setShowQuotationForm] = useState(false);
  const [showCashSaleForm, setShowCashSaleForm] = useState(false);
  const [quotationData, setQuotationData] = useState({
    customer: { name: '', phone: '', email: '' },
    lineItems: [{ serviceType: '', description: '', quantity: 1, unitPrice: 0 }],
    expectedDueDate: '',
    notes: ''
  });
  const [cashSaleData, setCashSaleData] = useState({
    customer: { name: '', phone: '', email: '' },
    lineItems: [{ serviceType: '', description: '', quantity: 1, unitPrice: 0 }],
    totalAmount: 0,
    paymentMethod: 'cash',
    notes: ''
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchDocumentTypes();
    fetchJobs();
  }, []);

  const fetchDocumentTypes = async () => {
    try {
      const data = await getDocumentTypes();
      setDocumentTypes(data.documentTypes);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching document types:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      setError(getApiErrorMessage(error, 'Failed to fetch document types'));
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const data = await getJobs();
      setJobs(data.jobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
    }
  };

  const generateDocument = async (type, jobId = null) => {
    try {
      const blobData = await generateDocumentBlob(type, {
        jobId,
        payload: type === 'quotation' ? quotationData : type === 'cash-sale' ? cashSaleData : undefined,
      });

      const blob = new Blob([blobData], { type: 'application/pdf' });
      const url2 = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url2;
      link.download = `${type}-${jobId || Date.now()}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url2);
    } catch (error) {
      console.error(`Failed to generate ${type}:`, error);
      setError(`Failed to generate ${type}`);
    }
  };

  const addLineItem = (formType) => {
    const newItem = { serviceType: '', description: '', quantity: 1, unitPrice: 0 };
    if (formType === 'quotation') {
      setQuotationData(prev => ({
        ...prev,
        lineItems: [...prev.lineItems, newItem]
      }));
    } else {
      setCashSaleData(prev => ({
        ...prev,
        lineItems: [...prev.lineItems, newItem]
      }));
    }
  };

  const updateLineItem = (index, field, value, formType) => {
    if (formType === 'quotation') {
      setQuotationData(prev => ({
        ...prev,
        lineItems: prev.lineItems.map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }));
    } else {
      setCashSaleData(prev => ({
        ...prev,
        lineItems: prev.lineItems.map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }));
    }
  };

  const removeLineItem = (index, formType) => {
    if (formType === 'quotation') {
      setQuotationData(prev => ({
        ...prev,
        lineItems: prev.lineItems.filter((_, i) => i !== index)
      }));
    } else {
      setCashSaleData(prev => ({
        ...prev,
        lineItems: prev.lineItems.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotal = (lineItems) => {
    return lineItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  };

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <BackButton to="/dashboard" />
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  const getDocumentIcon = (docType) => {
    switch(docType) {
      case 'invoice':
        return <DocumentTextIcon className="w-6 h-6" />;
      case 'receipt':
        return <ReceiptRefundIcon className="w-6 h-6" />;
      case 'delivery-note':
        return <TruckIcon className="w-6 h-6" />;
      default:
        return <DocumentTextIcon className="w-6 h-6" />;
    }
  };

  const getDocumentColor = (docType) => {
    switch(docType) {
      case 'invoice':
        return 'from-blue-500 to-blue-600';
      case 'receipt':
        return 'from-green-500 to-green-600';
      case 'delivery-note':
        return 'from-orange-500 to-orange-600';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-8">
        <BackButton to="/dashboard" />
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Document Generation</h1>
          <p className="text-gray-600">Generate professional invoices, receipts, delivery notes, and more</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {/* Job-based Documents */}
          <div className="relative overflow-hidden rounded-2xl shadow-xl border border-gray-200 bg-gradient-to-br from-white to-blue-50">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full -mr-16 -mt-16 opacity-20"></div>
            <div className="relative p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <DocumentTextIcon className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Job-based Documents</h2>
                  <p className="text-sm text-gray-600">Invoice, Receipt, Delivery Note</p>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Job
                </label>
                <div className="relative">
                  <select
                    value={selectedJob}
                    onChange={(e) => setSelectedJob(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm transition-all"
                  >
                    <option value="">Choose a job...</option>
                    {jobs.map(job => (
                      <option key={job._id} value={job._id}>
                        {job.jobId} - {job.customer?.name || 'N/A'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-3">
                {documentTypes.filter(doc => doc.requires === 'jobId').map(doc => (
                  <button
                    key={doc.id}
                    onClick={() => generateDocument(doc.id, selectedJob)}
                    disabled={!selectedJob}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r ${getDocumentColor(doc.id)} text-white rounded-xl hover:shadow-lg transform hover:-translate-y-0.5 transition-all disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed disabled:transform-none`}
                  >
                    <DocumentArrowDownIcon className="w-5 h-5" />
                    <span className="font-semibold">Generate {doc.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Quotation */}
          <div className="relative overflow-hidden rounded-2xl shadow-xl border border-gray-200 bg-gradient-to-br from-white to-green-50">
            <div className="absolute top-0 right-0 w-32 h-32 bg-green-200 rounded-full -mr-16 -mt-16 opacity-20"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <DocumentTextIcon className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Quotation</h2>
                    <p className="text-sm text-gray-600">Create price estimates</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowQuotationForm(!showQuotationForm)}
                  className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                    showQuotationForm 
                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                      : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:shadow-lg'
                  }`}
                >
                  {showQuotationForm ? 'Hide' : 'Create'}
                </button>
              </div>

              {showQuotationForm && (
                <div className="space-y-5">
                  {/* Customer Info Section */}
                  <div className="bg-white rounded-xl p-4 border-2 border-gray-100">
                    <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                      <UserIcon className="w-5 h-5 text-green-600" />
                      Customer Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Customer Name</label>
                        <div className="relative">
                          <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={quotationData.customer.name}
                            onChange={(e) => setQuotationData(prev => ({
                              ...prev,
                              customer: { ...prev.customer, name: e.target.value }
                            }))}
                            className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            placeholder="Enter customer name"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Customer Phone</label>
                        <div className="relative">
                          <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={quotationData.customer.phone}
                            onChange={(e) => setQuotationData(prev => ({
                              ...prev,
                              customer: { ...prev.customer, phone: e.target.value }
                            }))}
                            className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            placeholder="Enter phone number"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Due Date</label>
                        <div className="relative">
                          <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="date"
                            value={quotationData.expectedDueDate}
                            onChange={(e) => setQuotationData(prev => ({
                              ...prev,
                              expectedDueDate: e.target.value
                            }))}
                            className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                        <textarea
                          value={quotationData.notes}
                          onChange={(e) => setQuotationData(prev => ({
                            ...prev,
                            notes: e.target.value
                          }))}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          rows="3"
                          placeholder="Additional notes..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Line Items Section */}
                  <div className="bg-white rounded-xl p-4 border-2 border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                        <ShoppingBagIcon className="w-5 h-5 text-green-600" />
                        Line Items
                      </h3>
                      <button
                        onClick={() => addLineItem('quotation')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-all"
                      >
                        <PlusIcon className="w-5 h-5" />
                        <span className="font-semibold">Add Item</span>
                      </button>
                    </div>
                    <div className="space-y-3">
                      {quotationData.lineItems.map((item, index) => (
                        <div key={index} className="border-2 border-gray-200 rounded-xl p-4 bg-gray-50">
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <input
                              type="text"
                              placeholder="Service Type"
                              value={item.serviceType}
                              onChange={(e) => updateLineItem(index, 'serviceType', e.target.value, 'quotation')}
                              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                            <input
                              type="text"
                              placeholder="Description"
                              value={item.description}
                              onChange={(e) => updateLineItem(index, 'description', e.target.value, 'quotation')}
                              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                          </div>
                          <div className="grid grid-cols-4 gap-3">
                            <input
                              type="number"
                              placeholder="Qty"
                              value={item.quantity}
                              onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 0, 'quotation')}
                              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                            <input
                              type="number"
                              placeholder="Unit Price"
                              value={item.unitPrice}
                              onChange={(e) => updateLineItem(index, 'unitPrice', parseFloat(e.target.value) || 0, 'quotation')}
                              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                            <div className="flex items-center px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                              <span className="text-sm font-semibold text-green-700">
                                KSh {(item.quantity * item.unitPrice).toFixed(2)}
                              </span>
                            </div>
                            <button
                              onClick={() => removeLineItem(index, 'quotation')}
                              className="flex items-center justify-center px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Total and Generate */}
                  <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-5 text-white">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-lg font-semibold">Total Amount:</span>
                      <span className="text-2xl font-bold">KSh {calculateTotal(quotationData.lineItems).toFixed(2)}</span>
                    </div>
                    <button
                      onClick={() => generateDocument('quotation')}
                      className="w-full py-3 bg-white text-green-600 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <DocumentArrowDownIcon className="w-5 h-5" />
                        Generate Quotation PDF
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Cash Sale */}
          <div className="relative overflow-hidden rounded-2xl shadow-xl border border-gray-200 bg-gradient-to-br from-white to-purple-50">
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-200 rounded-full -mr-16 -mt-16 opacity-20"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <CurrencyDollarIcon className="w-8 h-8 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Cash Sale Receipt</h2>
                    <p className="text-sm text-gray-600">Immediate cash transactions</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCashSaleForm(!showCashSaleForm)}
                  className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                    showCashSaleForm 
                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                      : 'bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:shadow-lg'
                  }`}
                >
                  {showCashSaleForm ? 'Hide' : 'Create'}
                </button>
              </div>

              {showCashSaleForm && (
                <div className="space-y-5">
                  {/* Customer Info Section */}
                  <div className="bg-white rounded-xl p-4 border-2 border-gray-100">
                    <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                      <UserIcon className="w-5 h-5 text-purple-600" />
                      Customer Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Customer Name</label>
                        <div className="relative">
                          <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={cashSaleData.customer.name}
                            onChange={(e) => setCashSaleData(prev => ({
                              ...prev,
                              customer: { ...prev.customer, name: e.target.value }
                            }))}
                            className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Enter customer name"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Customer Phone</label>
                        <div className="relative">
                          <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={cashSaleData.customer.phone}
                            onChange={(e) => setCashSaleData(prev => ({
                              ...prev,
                              customer: { ...prev.customer, phone: e.target.value }
                            }))}
                            className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Enter phone number"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method</label>
                        <div className="relative">
                          <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <select
                            value={cashSaleData.paymentMethod}
                            onChange={(e) => setCashSaleData(prev => ({
                              ...prev,
                              paymentMethod: e.target.value
                            }))}
                            className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
                          >
                            <option value="cash">Cash</option>
                            <option value="till">Till</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                        <textarea
                          value={cashSaleData.notes}
                          onChange={(e) => setCashSaleData(prev => ({
                            ...prev,
                            notes: e.target.value
                          }))}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          rows="3"
                          placeholder="Additional notes..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Line Items Section */}
                  <div className="bg-white rounded-xl p-4 border-2 border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                        <ShoppingBagIcon className="w-5 h-5 text-purple-600" />
                        Line Items
                      </h3>
                      <button
                        onClick={() => addLineItem('cash-sale')}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-all"
                      >
                        <PlusIcon className="w-5 h-5" />
                        <span className="font-semibold">Add Item</span>
                      </button>
                    </div>
                    <div className="space-y-3">
                      {cashSaleData.lineItems.map((item, index) => (
                        <div key={index} className="border-2 border-gray-200 rounded-xl p-4 bg-gray-50">
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <input
                              type="text"
                              placeholder="Service Type"
                              value={item.serviceType}
                              onChange={(e) => updateLineItem(index, 'serviceType', e.target.value, 'cash-sale')}
                              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            <input
                              type="text"
                              placeholder="Description"
                              value={item.description}
                              onChange={(e) => updateLineItem(index, 'description', e.target.value, 'cash-sale')}
                              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                          <div className="grid grid-cols-4 gap-3">
                            <input
                              type="number"
                              placeholder="Qty"
                              value={item.quantity}
                              onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 0, 'cash-sale')}
                              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            <input
                              type="number"
                              placeholder="Unit Price"
                              value={item.unitPrice}
                              onChange={(e) => updateLineItem(index, 'unitPrice', parseFloat(e.target.value) || 0, 'cash-sale')}
                              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            <div className="flex items-center px-4 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                              <span className="text-sm font-semibold text-purple-700">
                                KSh {(item.quantity * item.unitPrice).toFixed(2)}
                              </span>
                            </div>
                            <button
                              onClick={() => removeLineItem(index, 'cash-sale')}
                              className="flex items-center justify-center px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Total and Generate */}
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-5 text-white">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-lg font-semibold">Total Amount:</span>
                      <span className="text-2xl font-bold">KSh {calculateTotal(cashSaleData.lineItems).toFixed(2)}</span>
                    </div>
                    <button
                      onClick={() => generateDocument('cash-sale')}
                      className="w-full py-3 bg-white text-purple-600 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <DocumentArrowDownIcon className="w-5 h-5" />
                        Generate Cash Sale PDF
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documents;
